import { PLACE_TYPES_BY_CATEGORY, PlaceCategory } from '@lib/placeTypes'
import { EventCategory } from '@orbit/api-types'

/**
 * The event category a place's Google `types` imply — the create-event flow's
 * guess, which it makes until the user picks one. Mirrors iOS
 * `EventCategory(googleTypes:)`: the place categories of `@lib/placeTypes`
 * (iOS `LocationPlaceType.category`), except where an event reads finer than
 * they do — a park is Nature, not Recreation; a venue is an Event, not Culture
 * or Sports. Only the place categories that say something about an event
 * count; a city, a bank or a school implies nothing.
 */

/** Types that read finer as an event than their place category does; checked first. */
const FINER: Record<string, EventCategory> = {
	park: 'Nature',
	national_park: 'Nature',
	state_park: 'Nature',
	hiking_area: 'Nature',
	garden: 'Nature',
	botanical_garden: 'Nature',
	natural_feature: 'Nature',
	campground: 'Nature',
	wildlife_park: 'Nature',
	wildlife_refuge: 'Nature',
	night_club: 'Event',
	concert_hall: 'Event',
	comedy_club: 'Event',
	event_venue: 'Event',
	stadium: 'Event',
	arena: 'Event',
	performing_arts_theater: 'Event',
	opera_house: 'Event',
	philharmonic_hall: 'Event',
	amphitheatre: 'Event',
	place_of_worship: 'SeeAndDo',
}

/** The place categories that say something about an event, as the event category each implies. */
const EVENT_CATEGORY_BY_PLACE_CATEGORY: Partial<Record<PlaceCategory, EventCategory>> = {
	foodAndDrink: 'FoodAndDrink',
	lodging: 'Accommodation',
	naturalFeatures: 'Nature',
	shopping: 'Shopping',
	transportation: 'Transportation',
	culture: 'SeeAndDo',
	entertainmentAndRecreation: 'SeeAndDo',
	placesOfWorship: 'SeeAndDo',
}

const CATEGORY_BY_TYPE = new Map<string, EventCategory>([
	...(Object.entries(EVENT_CATEGORY_BY_PLACE_CATEGORY) as [PlaceCategory, EventCategory][]).flatMap(
		([placeCategory, eventCategory]) =>
			PLACE_TYPES_BY_CATEGORY[placeCategory].map(type => [type, eventCategory] as const),
	),
	...Object.entries(FINER),
])

/** The category the first type that maps to one implies, or null when none does (a typed place has no types at all). */
export const categoryFromGoogleTypes = (types: readonly string[] | null | undefined): EventCategory | null => {
	for (const type of types ?? []) {
		const category = CATEGORY_BY_TYPE.get(type)
		if (category) return category
	}
	return null
}
